import { useState } from "react";
import { Button } from "@/components/ui/button";
import ResponsiveImage from "./ResponsiveImage";
import Logo from "./Logo";
import { useSectionData } from "@/hooks/useSectionData";
import { useTranslation } from "react-i18next";
import { useTranslatedContent } from "@/hooks/useTranslatedContent";

// Tiny blur placeholder inlined as base64 — shown until the admin image loads
const HERO_BLUR_PLACEHOLDER =
  "data:image/webp;base64,UklGRlIAAABXRUJQVlA4IEYAAADQAQCdASoUAA4AAkA4JZQCdAEO/hepgAD++OP/aj//Uf/0H5m9Gbsxk7n/0kzJvJWbJJt3dXqx1e8vVjq95erHV7wAAA==";

const HeroSection = () => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const { title, subtitle, description, image_url, mobile_image_url, buttons, isLoading } = useSectionData("hero");
  const { t } = useTranslation();
  const { t: td } = useTranslatedContent([title, subtitle, description, ...buttons.map(b => b.label)]);

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0">
        {/* Blur placeholder — painted instantly, hidden once real image loads */}
        <img
          src={HERO_BLUR_PLACEHOLDER}
          alt=""
          aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover scale-110 blur-lg transition-opacity duration-700 ${imageLoaded ? "opacity-0" : "opacity-100"}`}
        />
        {image_url && (
          <ResponsiveImage
            src={image_url}
            mobileSrc={mobile_image_url}
            alt="La Petite Cannoise - Beach Club à Cannes"
            className={`w-full h-full object-cover transition-opacity duration-700 ${imageLoaded ? "opacity-100" : "opacity-0"}`}
            sizes="100vw"
            width={1024}
            height={720}
            fetchPriority="high"
            loading="eager"
            decoding="auto"
            onLoad={() => setImageLoaded(true)}
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground/20 via-transparent to-background/80" />
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-32 pattern-waves opacity-60" />

      <div className="relative z-10 text-center px-4 max-w-4xl mx-auto animate-fade-in-up">
        <div className="bg-cream/90 backdrop-blur-sm rounded-t-[200px] px-8 py-16 md:px-16 md:py-20 shadow-elegant">
          <div className="mb-6">
            <Logo size="md" variant="color" className="mx-auto" fetchPriority="high" loading="eager" />
          </div>

          <p className="font-script text-xl md:text-2xl text-rose-dark mb-2">
            {td(subtitle) || t("hero.welcomeTo")}
          </p>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-sage-dark tracking-wide mb-4">
            {td(title) || t("hero.title")}
          </h1>
          <p className="font-display text-lg md:text-xl tracking-[0.3em] uppercase text-foreground/70 mb-8">
            {t("hero.beachClub")}
          </p>

          <p className="font-body text-lg md:text-xl text-foreground/80 max-w-2xl mx-auto mb-10 leading-relaxed">
            {td(description) || t("hero.defaultDescription")}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {buttons.length > 0 ? buttons.map((btn, i) => (
              <a key={i} href={btn.url} target={btn.url.startsWith("http") ? "_blank" : undefined} rel="noopener noreferrer">
                <Button variant={i === 0 ? "hero" : "hero-outline"} size="xl">
                  {td(btn.label)}
                </Button>
              </a>
            )) : (
              <>
                <Button variant="hero" size="xl">{t("hero.btnBeach")}</Button>
                <Button variant="hero-outline" size="xl">{t("hero.btnRestaurant")}</Button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-float">
        <div className="w-6 h-10 rounded-full border-2 border-sage flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-sage rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
